import FilterAltIcon from "@mui/icons-material/FilterAlt"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import QuizRoundedIcon from "@mui/icons-material/QuizRounded"
import {
    Box,
    Stack,
    Typography,
    Divider,
    useTheme,
    IconButton,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, ReactNode, useState } from "react"
import { useDispatch } from "react-redux"

import { Datasets } from "../../constants/enums"
import { TTR_DIALOG_MESSAGE } from "../../constants/messages"
import { useGetState } from "../../hooks/useGetState"
import { useGetUserData } from "../../hooks/useGetUserData"
import { useScreenSize } from "../../hooks/useScreenSize"
import {
    getShowSearchFilters,
    setShowSearchFilters,
} from "../../redux/preferencesSlice"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import AppButton from "../buttons/appButton"
import DownloadDatasetButton from "../buttons/downloadDatasetButton"
import {
    EVChargerIcon,
    HeatPumpIcon,
    ElectricBoilerIcon,
    AirConditioningIcon,
    SolarPanelIcon,
} from "../icons/icons"
import RevalidatingSpinner from "../loading/spinners/revalidatingSpinner"
import SimpleDialog from "../popUps/dialog/dialog"
import AppPaper from "../surfaces/appPaper"
import DisplayIfElse from "../wrappers/displayIfElse"

interface IFilterTabBarFrameProps {
    children?: ReactNode
    isValidating: boolean
    dataset: Datasets
    titleToDisplayInBaseColour: string
    titleToDisplayInPrimaryColour: string
}

export const DatasetIcons = (dataset: Datasets): ReactElement => {
    const mapping = {
        TTR: <SolarPanelIcon color="primary" fontSize="large" />,
        TTR_DRAFT: <SolarPanelIcon color="primary" fontSize="large" />,
        EVAC: <EVChargerIcon color="primary" fontSize="large" />,
        EVAC_DRAFT: <EVChargerIcon color="primary" fontSize="large" />,
        EVDC: <EVChargerIcon color="primary" fontSize="large" />,
        EVDC_DRAFT: <EVChargerIcon color="primary" fontSize="large" />,
        HP: <HeatPumpIcon color="primary" fontSize="large" />,
        HP_DRAFT: <HeatPumpIcon color="primary" fontSize="large" />,
        EB: <ElectricBoilerIcon color="primary" fontSize="large" />,
        EB_DRAFT: <ElectricBoilerIcon color="primary" fontSize="large" />,
        ACU: <AirConditioningIcon color="primary" fontSize="large" />,
        ACU_DRAFT: <AirConditioningIcon color="primary" fontSize="large" />,
    }
    return mapping[dataset]
}

const FilterFocusIcon = ({
    showFilters,
}: {
    showFilters: boolean
}): ReactElement =>
    showFilters ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />

/**
 * Abstracts layout for the panel tabs showing selected filters.
 */
const FilterTabBarFrame = ({
    children,
    isValidating,
    dataset,
    titleToDisplayInBaseColour,
    titleToDisplayInPrimaryColour,
}: IFilterTabBarFrameProps): ReactElement => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const showFilters = useGetState(getShowSearchFilters)
    const { isTabletOrMob, isMob } = useScreenSize()
    const { isEnaSysAdmin } = useGetUserData()
    const DeviceIcon = DatasetIcons(dataset)
    const [filterButtonFocus, setFilterButtonFocus] = useState(false)

    // Handle dialog open state
    const [open, setOpen] = useState(false)

    const handleClickOpen = (): void => {
        setOpen(true)
    }

    const handleClose = (): void => {
        setOpen(false)
    }

    return (
        <Grid xs={12}>
            <SimpleDialog
                open={open}
                onClose={handleClose}
                fullScreen={isMob}
                title="What is the Live Device Register?"
                message={TTR_DIALOG_MESSAGE}
            />
            <Stack
                direction="row"
                spacing={GRID_SPACING}
                alignItems="center"
                justifyContent="space-between"
            >
                <DisplayIfElse
                    condition={isTabletOrMob}
                    displayIfTrue={
                        <Typography color="text.primary" variant="h6">
                            {`${titleToDisplayInBaseColour} ${titleToDisplayInPrimaryColour}`}
                        </Typography>
                    }
                    displayIfFalse={
                        <Box display="flex" flexDirection="row">
                            <AppPaper
                                sx={{
                                    paddingY: theme.spacing(1),
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    {DeviceIcon}
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        justifyContent="center"
                                    >
                                        {titleToDisplayInBaseColour}{" "}
                                        <Box
                                            color={theme.palette.primary.main}
                                            display="inline"
                                        >
                                            {titleToDisplayInPrimaryColour}
                                        </Box>{" "}
                                    </Typography>
                                </Stack>
                            </AppPaper>
                            <IconButton onClick={handleClickOpen}>
                                <QuizRoundedIcon
                                    sx={{
                                        color: theme.palette.primary.main,
                                    }}
                                />
                            </IconButton>
                        </Box>
                    }
                />

                <Stack
                    direction="row"
                    spacing={GRID_SPACING}
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={2}
                    >
                        <RevalidatingSpinner isValidating={isValidating} />
                        {children}
                    </Stack>

                    <Divider orientation="vertical" flexItem />
                    {isEnaSysAdmin && (
                        <DownloadDatasetButton dataset={dataset} />
                    )}
                    <AppButton
                        onClick={() => dispatch(setShowSearchFilters())}
                        color="secondary"
                        endIcon={
                            filterButtonFocus ? (
                                <FilterFocusIcon showFilters={showFilters} />
                            ) : (
                                <FilterAltIcon />
                            )
                        }
                        onMouseEnter={() => setFilterButtonFocus(true)}
                        onMouseLeave={() => setFilterButtonFocus(false)}
                    >
                        Filters
                    </AppButton>
                </Stack>
            </Stack>
        </Grid>
    )
}

FilterTabBarFrame.defaultProps = {
    children: null,
}

export default FilterTabBarFrame
